import React from 'react';
import PrivacyPolicy from '../../components/PrivacyPolicy/PrivacyPolicy';

function PromptGruupPrivacy() {
  const sections = [
    {
      title: 'Introduction',
      content: (
        <>
          <p>
            PromptGruup is a visual workflow platform for interacting with large language models (LLMs).
            Users create, manage, and execute visual workflows composed of prompt templates and LLM
            interactions. This Privacy Policy explains how Castle Point Innovations LLC ("we," "us," or
            "our") collects, uses, stores, and protects your information when you use PromptGruup and its
            related services.
          </p>
          <p>
            By using PromptGruup, you agree to the collection and use of information as described in this
            policy. If you do not agree with any part of this policy, please discontinue use of the
            application.
          </p>
        </>
      ),
    },
    {
      title: 'Information We Collect',
      content: (
        <>
          <p>We collect the following types of information when you use PromptGruup:</p>
          <ul>
            <li>
              <strong>Account Information:</strong> If you create an account, we collect your email
              address and authentication credentials necessary to provide you with secure access to the
              platform.
            </li>
            <li>
              <strong>Workflow Data:</strong> We collect and store the visual workflow configurations,
              prompt templates, node arrangements, and related metadata that you create within the
              application.
            </li>
            <li>
              <strong>LLM Interaction Data:</strong> We store API responses and conversation history
              generated within your workflows, including inputs sent to and outputs received from LLM
              providers.
            </li>
            <li>
              <strong>Usage Analytics:</strong> We collect general usage metrics such as feature usage,
              workflow execution frequency, and interaction patterns to improve our services.
            </li>
          </ul>
        </>
      ),
    },
    {
      title: 'How We Use Your Information',
      content: (
        <>
          <p>We use the information we collect for the following purposes:</p>
          <ul>
            <li>To create and manage your account and authenticate your identity.</li>
            <li>To save, load, and execute your visual workflows and prompt templates.</li>
            <li>To facilitate communication between your workflows and configured LLM API providers.</li>
            <li>To analyze usage patterns and improve the functionality and performance of PromptGruup.</li>
            <li>To communicate with you regarding account updates, service changes, or support inquiries.</li>
            <li>To enforce our terms of service and protect against misuse of the platform.</li>
          </ul>
        </>
      ),
    },
    {
      title: 'Third-Party Services',
      content: (
        <>
          <p>
            PromptGruup integrates with third-party LLM API providers as configured by you. When you
            execute a workflow, prompt data and related inputs are transmitted to the LLM provider(s) you
            have selected. Each provider operates under its own privacy policy and terms of service.
          </p>
          <p>
            We encourage you to review the privacy policies of any LLM API providers you configure within
            PromptGruup. We are not responsible for the data practices of these third-party providers.
          </p>
        </>
      ),
    },
    {
      title: 'Data Storage and Security',
      content: (
        <>
          <p>
            Your workflow configurations, prompt templates, and associated data are stored using secure
            cloud infrastructure with encryption at rest and in transit. We implement reasonable technical
            and organizational measures to protect your data from unauthorized access, alteration,
            disclosure, or destruction.
          </p>
          <p>
            However, no method of electronic storage or transmission is completely secure, and we cannot
            guarantee absolute security. You are responsible for safeguarding any API keys or credentials
            you configure within the application.
          </p>
        </>
      ),
    },
    {
      title: 'Your Rights',
      content: (
        <>
          <p>Depending on your jurisdiction, you may have the following rights regarding your data:</p>
          <ul>
            <li>
              <strong>Access:</strong> You may request a copy of the personal data we hold about you.
            </li>
            <li>
              <strong>Deletion:</strong> You may request that we delete your account and all associated
              data, including workflows, prompt templates, and conversation history.
            </li>
            <li>
              <strong>Portability:</strong> You may request an export of your data in a commonly used,
              machine-readable format.
            </li>
            <li>
              <strong>Correction:</strong> You may request correction of any inaccurate personal data we
              hold about you.
            </li>
          </ul>
          <p>
            To exercise any of these rights, please contact us using the information provided in the
            Contact Us section below.
          </p>
        </>
      ),
    },
    {
      title: "Children's Privacy",
      content: (
        <>
          <p>
            PromptGruup is not intended for use by individuals under the age of 13. We do not knowingly
            collect personal information from children under 13. If we become aware that we have
            inadvertently collected data from a child under 13, we will take steps to delete that
            information promptly. If you believe a child under 13 has provided us with personal data,
            please contact us immediately.
          </p>
        </>
      ),
    },
    {
      title: 'Changes to This Policy',
      content: (
        <>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices,
            technologies, or legal requirements. When we make material changes, we will update the "Last
            Updated" date at the top of this policy and, where appropriate, notify you via email or through
            the application. We encourage you to review this policy periodically.
          </p>
        </>
      ),
    },
    {
      title: 'Contact Us',
      content: (
        <>
          <p>
            If you have any questions, concerns, or requests regarding this Privacy Policy or our data
            practices, please contact us at:
          </p>
          <p>
            <strong>Email:</strong> contact@castlepointinnovations.com
          </p>
        </>
      ),
    },
  ];

  return <PrivacyPolicy productName="PromptGruup" lastUpdated="March 27, 2026" sections={sections} />;
}

export default PromptGruupPrivacy;
